import type { DashboardLayoutProps } from '@classroom-ai/ui/layouts/dashboard';

import { Label } from '@classroom-ai/ui/components/label';
import { useAuthContext } from '@classroom-ai/ui/auth/hooks';
import { DashboardLayout as UiDashboardLayout } from '@classroom-ai/ui/layouts/dashboard';

import Box from '@mui/material/Box';

import { useLeadScope } from 'src/class/use-lead-scope';
import { ClassProvider, useActiveClass, useActiveCourse } from 'src/class/class-provider';

import { useWrapUp } from 'src/sections/week/use-wrap-up';
import { AskAiButton } from 'src/sections/ask-ai/ask-ai-button';
import { AskAiProvider } from 'src/sections/ask-ai/ask-ai-provider';

import { usePermissions } from 'src/auth/use-permissions';

import { ViewingBanner } from '../viewing-banner';
import { getNavData } from '../nav-config-dashboard';
import { HeaderClassTitle } from '../header/header-class-title';
import { AccountSchoolYear } from '../header/account-school-year';

// ----------------------------------------------------------------------

const NO_CLASSES: never[] = [];

export { DashboardContent } from '@classroom-ai/ui/layouts/dashboard';

/**
 * Teacher wrapper around the shared dashboard layout — injects this app's nav data and the
 * app-wide class title. No workspaces/account demo data (unlike the admin app).
 *
 * `ClassProvider` wraps the whole layout so both the header class title (`HeaderClassTitle`)
 * and the page content (`children`) share one active-school/class/course selection. The
 * nav-building lives in `DashboardLayoutInner` — it must run **inside** the provider because
 * `usePermissions()` → `useActiveCourse()` throws outside it; the provider can't wrap a hook
 * called in this component's own body, hence the split.
 */
export function DashboardLayout(props: DashboardLayoutProps) {
  return (
    <ClassProvider>
      <DashboardLayoutInner {...props} />
    </ClassProvider>
  );
}

function DashboardLayoutInner({ slotProps, children, ...other }: DashboardLayoutProps) {
  const { user } = useAuthContext();
  const perms = usePermissions();
  const { activeSchoolYearId } = useActiveCourse();
  const { scope } = useLeadScope(activeSchoolYearId);
  const isDistrictAdmin = user?.role === 'DISTRICT_ADMIN';
  const { classes } = useActiveClass();
  // Epic #647: my own lessons still waiting for a "Mark taught" (earlier days; today's once
  // the day is over) — the same read as the Dashboard's Wrap up card.
  const wrap = useWrapUp(isDistrictAdmin ? NO_CLASSES : classes);
  const dashboardBadge = wrap.rows.length ? (
    <Label color="warning" variant="soft" title={`${wrap.rows.length} lesson${wrap.rows.length === 1 ? '' : 's'} not marked taught`}>
      {wrap.rows.length}
    </Label>
  ) : undefined;

  return (
    <AskAiProvider>
      <UiDashboardLayout
        {...other}
        slotProps={{
          nav: { data: getNavData(user?.role, perms, { showDepartment: scope.hasLeadAccess, dashboardBadge }) },
          account: isDistrictAdmin ? undefined : { extra: <AccountSchoolYear /> },
          header: {
            // Let the class title shrink (ellipsis) instead of pushing the header wider on phones.
            slotProps: { centerArea: { style: { minWidth: 0 } } },
            slots: {
              // Class-first header (epic #617): the class you're teaching reads as the title on
              // the left (no school picker — the school and year live in its menu footer and
              // the account drawer); Beacon sits at the right, before the shared
              // settings/account buttons (the right slot is owned by packages/ui).
              // District admins have no class context, so the header stays empty (epic #76).
              centerArea: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 1, minWidth: 0 }}>
                  {!isDistrictAdmin && <HeaderClassTitle />}
                  <Box sx={{ flex: 1 }} />
                  <AskAiButton />
                </Box>
              ),
            },
          },
          ...slotProps,
        }}
      >
        <ViewingBanner />
        {children}
      </UiDashboardLayout>
    </AskAiProvider>
  );
}
